import { Box } from "@mui/material";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_MEDIA,
  SITE_TYPE,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { LinkCard } from "../siteKit/LinkCard";
import { SiteFileImage } from "../siteKit/media/SiteFileImage";
import type { SiteFileKey } from "../siteKit/media/siteMedia";
import { revealRef } from "../siteKit/motion/reveal";
import { SiteSection } from "../siteKit/SiteSection";
import { SiteTitle } from "../siteKit/SiteText";

const NEWS_RELATED_STYLES = {
  // 앞 칸(기사 목록·기사)의 아래 여백이 구역 사이 간격을 맡는다 — 위 여백까지 두면 두 배로 벌어진다.
  section: { paddingTop: 0 },
  title: SITE_TYPE.h3,
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: "clamp(24px,3vw,40px)",
    marginTop: "clamp(32px,4vw,48px)",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 한 줄에 하나면 카드 그림 하나가 화면 두 개 높이다 — 넓은 화면처럼 두 칸
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    },
  },
} as const;

/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 이용 플랜 「함께 보면」과 같은 카드 두 장 */
const RELATED_OFFSCREEN_SIZE = [900, 1300] as const;

const RELATED_CARDS = [
  { key: "platform", href: "/accurate", art: "art/tile_platform.svg" },
  { key: "releases", href: "/releases", art: "art/tile_releases.svg" },
] as const satisfies ReadonlyArray<{
  key: string;
  href: string;
  art: SiteFileKey;
}>;

/** 언론보도 목록·상세 맨 아래 「함께 보면 좋아요」(press-news): 서비스 플랫폼·업데이트 소식으로 가는 사이트 안 카드 두 장. */
export function NewsRelatedLinks() {
  const { t } = useTranslation("landing");
  const titleId = useId();
  return (
    <SiteSection
      labelledBy={titleId}
      skipOffscreen={RELATED_OFFSCREEN_SIZE}
      sx={NEWS_RELATED_STYLES.section}
    >
      <Box sx={SITE_WRAP}>
        <Box
          component="h2"
          id={titleId}
          ref={revealRef}
          data-reveal="up"
          sx={NEWS_RELATED_STYLES.title}
        >
          <SiteTitle text={t("site.news.related.title")} />
        </Box>
        <Box sx={NEWS_RELATED_STYLES.grid}>
          {RELATED_CARDS.map((card, index) => (
            <LinkCard
              key={card.key}
              href={card.href}
              revealIndex={index}
              media={
                <SiteFileImage
                  file={card.art}
                  width={600}
                  height={400}
                  alt={t(`site.news.related.${card.key}.alt`)}
                />
              }
              kicker={t(`site.news.related.${card.key}.kicker`)}
              title={t(`site.news.related.${card.key}.title`)}
              text={t(`site.news.related.${card.key}.text`)}
            />
          ))}
        </Box>
      </Box>
    </SiteSection>
  );
}
